import { getCookie } from '@/helpers/cookieMetods';
import type {
  CreateTaskArgs,
  CreateTaskResponse,
  GetTaskReviewsArgs,
  GetUserTasksArgs,
  ProcessTaskReviewArgs,
  SubmitTaskProofArgs,
  TaskActionResponse,
  TaskFormData,
  TaskReportRow,
  TaskReviewRow,
  UserTaskRow,
  UserTasks,
} from '../types/tasks';
import type { EntityId } from '../types/common';
import { apiUrl, domain, parseJsonOrThrow } from './http';

interface TaskListResponse<T> extends TaskActionResponse {
  data?: T[] | null;
}

/**
 * Every task endpoint requires `Authorization: Bearer <token>` (the user is
 * identified by the backend's `_prt_uid_num` cookie). The spec calls it an API
 * key; until the backend says otherwise we send the session `_form_token`, as
 * `shop/variants.ts` does.
 */
const taskHeaders = (
  extra?: Record<string, string>
): Record<string, string> => ({
  Authorization: `Bearer ${getCookie('_form_token') ?? ''}`,
  ...extra,
});

const JSON_HEADERS = { 'Content-Type': 'application/json' };

/** Parse the JSON body, throwing on `status: 'error'`. */
const parseTaskResponse = async <T extends TaskActionResponse>(
  res: Response,
  fallbackError: string
): Promise<T> => {
  const result = await parseJsonOrThrow<T>(res);
  if (result.status === 'error') {
    throw new Error(result.message ?? fallbackError);
  }
  return result;
};

/** Build multipart body, skipping `undefined` fields. */
const toFormData = (
  fields: Record<string, string | number | Blob | undefined>
) => {
  const data = new FormData();
  for (const [key, value] of Object.entries(fields)) {
    if (value === undefined) continue;
    data.append(key, value instanceof Blob ? value : String(value));
  }
  return data;
};

const withQuery = (
  path: string,
  params: Record<string, string | undefined>
) => {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value) query.set(key, value);
  }
  const qs = query.toString();
  return apiUrl(qs ? `${path}?${qs}` : path);
};

/** Absolute URL for a review's `proof_file` (the API returns it relative to its domain). */
export const taskProofUrl = (path: string): string =>
  /^https?:\/\//.test(path) ? path : `${domain}/${path.replace(/^\/+/, '')}`;

/** POST — every task with paid coins, approved completion count and per-user details. */
export const getTaskReport = async (): Promise<TaskReportRow[]> => {
  const res = await fetch(apiUrl('/php/get_task_report.php'), {
    method: 'POST',
    headers: taskHeaders(JSON_HEADERS),
    body: '{}',
  });
  const result = await parseTaskResponse<TaskListResponse<TaskReportRow>>(
    res,
    'Не вдалося завантажити звіт по завданнях'
  );
  return result.data ?? [];
};

/** GET — submissions for tasks the current moderator created (backend default: `pending`). */
export const getTaskReviews = async ({
  status,
}: GetTaskReviewsArgs = {}): Promise<TaskReviewRow[]> => {
  const res = await fetch(withQuery('/php/get_task_reviews.php', { status }), {
    headers: taskHeaders(),
  });
  const result = await parseTaskResponse<TaskListResponse<TaskReviewRow>>(
    res,
    'Не вдалося завантажити завдання на перевірку'
  );
  return result.data ?? [];
};

/** GET — tasks available to the session user, one row per task with the latest submission. */
export const getUserTasks = async ({
  status,
  date_from,
  date_to,
}: GetUserTasksArgs = {}): Promise<UserTasks> => {
  const res = await fetch(
    withQuery('/php/get_user_tasks.php', { status, date_from, date_to }),
    { headers: taskHeaders() }
  );
  const result = await parseTaskResponse<
    TaskListResponse<UserTaskRow> & { user_id?: EntityId }
  >(res, 'Не вдалося завантажити завдання');
  return { userId: result.user_id ?? null, rows: result.data ?? [] };
};

/** POST JSON `{ review_id, action, comment? }` — approve (credits coins) or reject (comment required). */
export const processTaskReview = async (
  args: ProcessTaskReviewArgs
): Promise<TaskActionResponse> => {
  const res = await fetch(apiUrl('/php/process_task_review.php'), {
    method: 'POST',
    headers: taskHeaders(JSON_HEADERS),
    body: JSON.stringify(args),
  });
  return parseTaskResponse<TaskActionResponse>(
    res,
    'Помилка перевірки завдання'
  );
};

/** GET — departments tree and active employees for the create form's assignee picker. */
export const getTaskFormData = async (): Promise<TaskFormData> => {
  const res = await fetch(apiUrl('/php/add_task_form_data.php'), {
    headers: taskHeaders(),
  });
  const result = await parseTaskResponse<
    TaskActionResponse & Partial<TaskFormData>
  >(res, 'Не вдалося завантажити список співробітників');
  return { tree: result.tree ?? [], users: result.users ?? [] };
};

/** POST JSON — create a task (and its assignees for `target_type: 'selected'`). */
export const createTask = async (
  args: CreateTaskArgs
): Promise<CreateTaskResponse> => {
  const res = await fetch(apiUrl('/php/process_add_task.php'), {
    method: 'POST',
    headers: taskHeaders(JSON_HEADERS),
    body: JSON.stringify(args),
  });
  return parseTaskResponse<CreateTaskResponse>(
    res,
    'Не вдалося створити завдання'
  );
};

/**
 * POST (multipart) — employee's proof of completion: `comment` (text/link)
 * and/or a `screenshot` file (≤ 5 MB, JPG/PNG/WEBP/PDF).
 */
export const submitTaskProof = async (
  args: SubmitTaskProofArgs
): Promise<TaskActionResponse> => {
  const res = await fetch(apiUrl('/php/submit_task_proof.php'), {
    method: 'POST',
    headers: taskHeaders(),
    body: toFormData({ ...args }),
  });
  return parseTaskResponse<TaskActionResponse>(
    res,
    'Не вдалося відправити підтвердження'
  );
};
